import React, { useState, useEffect, useRef } from 'react';

import { useLanguage } from '@/contexts/LanguageContext';
import { useCursor } from '@/contexts/CursorContext';
import { VideoModal } from '@/components/molecules';
import { useGSAP } from '@/hooks/useGSAP';

import styles from './ShowReel.module.css';

const SHOWREEL_VIDEO = '/videos/showreel.mp4';

interface ShowReelProps {
  className?: string;
}

export const ShowReel: React.FC<ShowReelProps> = ({ className }) => {
  const { t } = useLanguage();
  const { setState } = useCursor();
  const { gsap, ready } = useGSAP();

  const [isMobile, setIsMobile] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Refs
  const sectionRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLDivElement>(null);
  const textWrapperRef = useRef<HTMLDivElement>(null);
  const miniVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = miniVideoRef.current;
    if (!video) return;

    if (isModalOpen) {
      video.pause();
    } else {
      video.play();
    }
  }, [isModalOpen]);

  // Hover cursor
  const onMouseEnter = () => {
    setState('other');
  };
  const onMouseLeave = () => {
    setState('default');
  };

  // Breakpoint
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Desktop animation (scale + translate3d)
  useEffect(() => {
    if (!ready || !gsap) return;
    if (isMobile || !sectionRef.current) return;

    const iframeEl = iframeRef.current;
    const textWrapperEl = textWrapperRef.current;
    const mm = gsap.matchMedia();

    const height = window.innerHeight;
    const width = window.innerWidth;
    const translationWidth = (width - 1340) / 2; // différence entre la largeur de l'écran et 1340px, divisé par 2 pour prendre en compte les deux côtés
    const translationHeight = height * 0.3; // Ajustez ce facteur pour contrôler la distance de translation

    mm.add('(min-width: 769px)', () => {
      const timeline = gsap.timeline();

      gsap.set(iframeEl, {
        scaleX: 0.2,
        scaleY: 0.2,
        translateX: translationWidth,
        translateY: -translationHeight,
        transformOrigin: 'top right',
        borderRadius: '1.2rem',
        force3D: true,
      });

      gsap.set(textWrapperEl, {
        width: '20%',
        translateX: translationWidth,
        translateY: -translationHeight,
        transformOrigin: 'top right',
        ease: 'power2.inOut',
        duration: 1.5,
        force3D: true,
      });

      timeline.to(iframeEl, {
        scaleX: 1,
        scaleY: 1,
        ease: 'power2.inOut',
        translateX: 0,
        translateY: 0,
        transformOrigin: 'top right',
        borderRadius: '0rem',
        scrollTrigger: {
          trigger: wrapperRef.current!.parentElement,
          start: 'top 30%',
          end: 'bottom bottom',
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });

      timeline.to(
        textWrapperEl,
        {
          width: '100%',
          ease: 'power2.inOut',
          translateX: 0,
          translateY: 0,
          transformOrigin: 'top right',
          scrollTrigger: {
            trigger: wrapperRef.current!.parentElement,
            start: 'top 30%',
            end: 'bottom bottom',
            scrub: 1,
            invalidateOnRefresh: true,
          },
        },
        0
      ); // Start at the same time as the iframe animation

      return () => {
        timeline.kill();
      };
    });

    return () => {
      mm.revert();
    };
  }, [isMobile, ready, gsap]);

  // Fade in container
  useEffect(() => {
    if (!ready || !gsap) return;
    if (isMobile || !containerRef.current) return;
    gsap.set(containerRef.current, { autoAlpha: 0 });
    gsap.to(containerRef.current, {
      autoAlpha: 1,
      duration: 1,
      delay: 0.6,
      ease: 'power3.out',
    });
  }, [isMobile, ready, gsap]);

  return (
    <div
      className={styles.showReelWrapper}
      id="showreel-section"
      ref={wrapperRef}
    >
      <section
        className={`${styles.showReelSection} ${className || ''}`}
        aria-label="ShowReel Section"
        ref={sectionRef}
      >
        <div className={styles.container} ref={containerRef}>
          {isMobile ? (
            <div className={styles.mobileContent}>
              <div className={styles.mobileVideoWrapper}>
                {/* Placeholder fixe pour éviter CLS */}
                <img
                  src="/assets/second-cover.webp"
                  alt="ShowReel Background"
                  className={styles.backgroundImageMobile}
                  loading="eager"
                  style={{ width: '100%', display: 'block' }}
                  width="335"
                  height="490"
                />
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster="/assets/second-cover.webp"
                  className={styles.mobileVideoIframe}
                >
                  <source src="/videos/showreel.webm" type="video/webm" />
                  <source src={SHOWREEL_VIDEO} type="video/mp4" />
                </video>
              </div>
            </div>
          ) : (
            <div
              className={styles.showReelContainerDesktop}
              aria-label="Play showreel"
            >
              <div className={styles.showreelContent}>
                <div className={styles.showreelVideoContainer}>
                  <div className={styles.textwrapper} ref={textWrapperRef}>
                    <div className={styles.textInner}>
                      <h2 className={styles.showreelText}>
                        {t('home.showreel.title')}
                      </h2>
                      <h3 className={styles.showreelText}>2026</h3>
                    </div>
                  </div>
                  <div
                    className={styles.iframeWrapper}
                    ref={iframeRef}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={() => setIsModalOpen(true)}
                  >
                    <div className={styles.iframeInner}>
                      <video
                        ref={miniVideoRef}
                        autoPlay
                        muted
                        loop
                        playsInline
                        poster="/assets/second-cover.webp"
                      >
                        <source src="/videos/showreel.webm" type="video/webm" />
                        <source src={SHOWREEL_VIDEO} type="video/mp4" />
                      </video>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <VideoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        videoUrl={SHOWREEL_VIDEO}
        title="Showreel"
      />
    </div>
  );
};
